import {
  Box,
  CircularProgress,
  FormControllerContext,
  FormDatePicker,
  useTheme,
} from "common/components";
import { addFlowStepPrefix } from "common/utils";
import {
  ChangeProductStatusFlowData,
  ChangeProductStatusFlowStep,
  extractChangeEntityStatusFlowData,
  extractChangeEntityStatusFlowStep,
  ProductLifecycleActionMode,
} from "core/api";
import { useTranslation } from "i18n";
import { ReactElement, useContext, useState } from "react";
import { useFormState } from "react-hook-form";
import { ProductLifecycleTimeline } from "../../../../components";
import { ChangeEntityStatus } from "../ChangeEntityStatus";
import { FlowStepComponentProps } from "../models";

export type ChangeProductStatusProps = FlowStepComponentProps<
  ChangeProductStatusFlowStep,
  ChangeProductStatusFlowData
>;

/** Change product status step component which is a status + order attributes. */
export function ChangeProductStatus({
  control,
  data,
  flowStep,
  isLoading,
  ...props
}: ChangeProductStatusProps): ReactElement {
  const { t } = useTranslation(["common"]);
  const theme = useTheme();
  const [isLifecycleLoading, setIsLifecycleLoading] = useState(false);
  const { evaluateOnChange } = useContext(FormControllerContext);
  const { errors } = useFormState({ control });
  const hasFormErrors = Object.keys(errors).length > 0;

  const { previewMode, showProductLifecycleTimeline } = flowStep.config;
  const { productLifecycleData, currentDate } = data;

  const handleEffectiveDateChange = () => {
    if (
      showProductLifecycleTimeline &&
      productLifecycleData?.actionMode ===
        ProductLifecycleActionMode.DEACTIVATE &&
      !hasFormErrors
    ) {
      setIsLifecycleLoading(true);
      // No need to pass an onComplete function to reset the isLifecycleLoading to false,
      // because the whole step gets re-mounted when the response is received
      // => state gets re-initialized.
      evaluateOnChange?.();
    }
  };

  function EmbeddedProductLifecycle({
    control,
    flowStep,
  }: ChangeProductStatusProps): ReactElement {
    const handleDemoDateChange = () => {
      // Trigger an evaluation request ONLY IF the ProductLifecycleTimeline
      // is displayed in this step, and previewMode is enabled
      if (showProductLifecycleTimeline && previewMode && !hasFormErrors) {
        setIsLifecycleLoading(true);
        // No need to pass an onComplete function to reset the isLifecycleLoading to false,
        // because the whole step gets re-mounted when the response is received
        // => state gets re-initialized.
        evaluateOnChange?.();
      }
    };

    const currentDateFieldName = addFlowStepPrefix(flowStep, "currentDate");

    return (
      <>
        {showProductLifecycleTimeline && (
          <>
            {isLifecycleLoading ? (
              <CircularProgress />
            ) : (
              <Box display="block">
                {previewMode && (
                  <FormDatePicker
                    control={control}
                    label={t("common:productLifeCycle.currentDate")}
                    fieldName={currentDateFieldName}
                    defaultValue={currentDate}
                    required
                    disabled={!previewMode || isLifecycleLoading}
                    onAccept={handleDemoDateChange}
                    onSelectedSectionsChange={(segment) => {
                      if (segment === null) {
                        // segment is null when the text input box of the Date field loses the focus
                        handleDemoDateChange();
                      }
                    }}
                    slotProps={{
                      textField: {
                        sx: { width: "190px" },
                      },
                    }}
                  />
                )}
                <ProductLifecycleTimeline
                  productLifecycleData={productLifecycleData}
                  timelineZIndex={theme.zIndex.modal}
                />
              </Box>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <>
      {isLoading ? (
        <CircularProgress />
      ) : (
        <ChangeEntityStatus
          control={control}
          data={extractChangeEntityStatusFlowData(data)}
          flowStep={extractChangeEntityStatusFlowStep(flowStep)}
          isLoading={isLoading}
          {...props}
          embedded={
            showProductLifecycleTimeline
              ? EmbeddedProductLifecycle({
                  control,
                  data,
                  flowStep,
                  isLoading,
                  ...props,
                })
              : undefined
          }
          handleEffectiveDateChange={handleEffectiveDateChange}
        />
      )}
    </>
  );
}
